import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface KanbanCard {
  id: string;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
}

export interface KanbanColumn {
  id: string;
  title: string;
  cards: KanbanCard[];
}

export interface KanbanBoard {
  columns: KanbanColumn[];
}

interface KanbanProps {
  board: KanbanBoard;
  onCardMove?: (
    card: KanbanCard,
    source: KanbanColumn,
    destination: KanbanColumn
  ) => void;
  onCardClick?: (card: KanbanCard) => void;
  renderCard?: (card: KanbanCard) => React.ReactNode;
  disableCardDrag?: boolean;
  columnStyle?:
    | React.CSSProperties
    | ((columnId: string) => React.CSSProperties);
  cardStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  containerClassName?: string;
  height?: string;
}

interface SortableCardProps {
  card: KanbanCard;
  renderCard?: (card: KanbanCard) => React.ReactNode;
  onCardClick?: (card: KanbanCard) => void;
  cardStyle?: React.CSSProperties;
}

const SortableCard: React.FC<SortableCardProps> = ({
  card,
  renderCard,
  onCardClick,
  cardStyle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  const defaultRenderCard = (card: KanbanCard) => (
    <Card
      size="sm"
      className="tw:mb-2 tw:cursor-pointer tw:shadow-sm"
      style={{ ...cardStyle, cursor: 'pointer' }}
    >
      <CardContent className="tw:px-3 tw:py-2">
        <h6 className="tw:mb-2 tw:text-sm tw:font-medium">{card.title}</h6>
        {card.description && (
          <p className="tw:mb-0 tw:text-xs tw:text-muted-foreground">
            {card.description}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onCardClick?.(card)}
    >
      {renderCard ? renderCard(card) : defaultRenderCard(card)}
    </div>
  );
};

interface DroppableColumnProps {
  column: KanbanColumn;
  children: React.ReactNode;
  columnStyle?: React.CSSProperties;
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({
  column,
  children,
  columnStyle,
}) => {
  const { setNodeRef, isOver } = useSortable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        ...columnStyle,
        transition: 'background-color 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      className={cn(
        'kanban-column tw:rounded-lg tw:border tw:border-border tw:bg-card tw:text-card-foreground',
        isOver && 'tw:bg-muted'
      )}
    >
      <div className="tw:mb-3 tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:border-b tw:border-border tw:pb-2">
        <h5 className="tw:mb-0 tw:text-base tw:font-medium tw:text-foreground">
          {column.title}
        </h5>
        <Badge variant="secondary">{column.cards.length}</Badge>
      </div>
      <div
        className="kanban-cards tw:flex-1 tw:overflow-x-hidden tw:overflow-y-auto tw:pr-1"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--border) transparent',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const Kanban: React.FC<KanbanProps> = ({
  board,
  onCardMove,
  onCardClick,
  renderCard,
  disableCardDrag = false,
  columnStyle,
  cardStyle,
  containerStyle,
  containerClassName = 'kanban-container',
  height = 'calc(100vh - 200px)',
}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [, setOverId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findContainer = (id: UniqueIdentifier) => {
    if (board.columns.some((column) => column.id === id)) {
      return id;
    }

    return board.columns.find((column) =>
      column.cards.some((card) => card.id === id)
    )?.id;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) return;

    setOverId(over.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over || disableCardDrag) return;

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id) || over.id;

    if (!activeContainer || !overContainer) return;

    const sourceColumn = board.columns.find(
      (col) => col.id === activeContainer
    );
    const destColumn = board.columns.find((col) => col.id === overContainer);
    const card = sourceColumn?.cards.find((c) => c.id === active.id);

    if (
      sourceColumn &&
      destColumn &&
      card &&
      activeContainer !== overContainer
    ) {
      onCardMove?.(card, sourceColumn, destColumn);
    }
  };

  const getColumnStyle = (columnId: string) => {
    const defaultStyle: React.CSSProperties = {
      borderRadius: '8px',
      padding: '1rem',
      margin: '0 0.5rem',
      flex: '1 1 0',
      minWidth: '300px',
      minHeight: '100%',
      maxHeight: '100%',
      overflowY: 'auto' as const,
      display: 'flex',
      flexDirection: 'column',
    };

    if (typeof columnStyle === 'function') {
      return { ...defaultStyle, ...columnStyle(columnId) };
    }

    return { ...defaultStyle, ...columnStyle };
  };

  const activeCard = board.columns
    .flatMap((col) => col.cards)
    .find((card) => card.id === activeId);

  const defaultContainerStyle: React.CSSProperties = {
    width: '100%',
    height: height,
    padding: '16px',
    marginBottom: '16px',
    ...containerStyle,
  };

  return (
    <div className={containerClassName} style={defaultContainerStyle}>
      <style>{`
        .${containerClassName} {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .${containerClassName} .kanban-board {
          display: flex !important;
          width: 100% !important;
          gap: 1rem;
          height: 100%;
          align-items: stretch;
          overflow-x: auto;
        }
        .${containerClassName} .kanban-column {
          flex: 1 1 0 !important;
          min-width: 300px !important;
          display: flex;
          flex-direction: column;
        }
        .${containerClassName} .kanban-column-header {
          flex-shrink: 0;
        }
        .${containerClassName} .kanban-column-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
        }
      `}</style>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          className="kanban-board tw:flex"
          style={{ gap: '1rem', height: '100%', width: '100%' }}
        >
          {board.columns.map((column) => (
            <DroppableColumn
              key={column.id}
              column={column}
              columnStyle={getColumnStyle(column.id)}
            >
              <SortableContext
                items={column.cards.map((card) => card.id)}
                strategy={verticalListSortingStrategy}
              >
                {column.cards.map((card) => (
                  <SortableCard
                    key={card.id}
                    card={card}
                    renderCard={renderCard}
                    onCardClick={onCardClick}
                    cardStyle={cardStyle}
                  />
                ))}
              </SortableContext>
            </DroppableColumn>
          ))}
        </div>
        <DragOverlay>
          {activeCard && renderCard ? (
            renderCard(activeCard)
          ) : activeCard ? (
            <Card
              size="sm"
              className="tw:shadow-lg"
              style={{ ...cardStyle, cursor: 'grabbing', opacity: 0.9 }}
            >
              <CardContent className="tw:px-3 tw:py-2">
                <h6 className="tw:mb-2 tw:text-sm tw:font-medium">
                  {activeCard.title}
                </h6>
                {activeCard.description && (
                  <p className="tw:mb-0 tw:text-xs tw:text-muted-foreground">
                    {activeCard.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default Kanban;
